import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ActiveProjectProvider } from "@/contexts/ActiveProjectContext";
import { ProcessingQueueProvider } from "@/contexts/ProcessingQueueContext";
import { TourProvider } from "@/contexts/TourContext";

// Eagerly import landing page for fastest FCP/LCP
import Index from "./pages/Index";

// Lazy-load all other pages so their CSS splits into separate chunks
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const UserRoleScreen = lazy(() => import("./components/onboarding/UserRoleScreen"));
const OnboardingFlow = lazy(() => import("./pages/OnboardingFlow"));
const OnboardingUpload = lazy(() => import("./pages/OnboardingUpload"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectView = lazy(() => import("./pages/ProjectView"));
const TranscriptLibrary = lazy(() => import("./pages/TranscriptLibrary"));
const Settings = lazy(() => import("./pages/Settings"));
const ResearchAdvisor = lazy(() => import("./pages/ResearchAdvisor"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Inbox = lazy(() => import("./pages/Inbox"));

const Findings = lazy(() => import("./pages/Findings"));
const Draft = lazy(() => import("./pages/Draft"));
const WhatIsVerithos = lazy(() => import("./pages/WhatIsVerithos"));
const SampleReport = lazy(() => import("./pages/SampleReport"));
const HowToCite = lazy(() => import("./pages/HowToCite"));
const VCardPage = lazy(() => import("./pages/VCardPage"));
const VBoardPage = lazy(() => import("./pages/VBoardPage"));
const VDraftPage = lazy(() => import("./pages/VDraftPage"));
const VerithosVsLLMs = lazy(() => import("./pages/VerithosVsLLMs"));
const QdasComparison = lazy(() => import("./pages/QdasComparison"));
const Whitepaper = lazy(() => import("./pages/Whitepaper"));
const Resources = lazy(() => import("./pages/Resources"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const InvitePage = lazy(() => import("./pages/InvitePage"));

import { ScrollToTop } from "./components/ScrollToTop";

// Top progress bar for lazy route loading
const PageLoader = () => (
  <div className="min-h-screen">
    <div className="fixed top-0 left-0 right-0 z-[100] h-0.5 bg-primary/10">
      <div className="h-full bg-primary animate-[progress_1.5s_ease-in-out_infinite] rounded-full" />
    </div>
  </div>
);

// Query client with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: false,
      refetchOnReconnect: true,
    },
  },
});

/**
 * AuthenticatedShell — wraps app routes with heavy providers
 * (Subscription, ActiveProject, ProcessingQueue).
 * Public routes skip these entirely for faster load.
 */
function AuthenticatedShell() {
  return (
    <SubscriptionProvider>
      <ActiveProjectProvider>
        <ProcessingQueueProvider>
          <TourProvider>
            <Outlet />
          </TourProvider>
        </ProcessingQueueProvider>
      </ActiveProjectProvider>
    </SubscriptionProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* ── Public routes — no heavy providers ── */}
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/logout" element={<Auth />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/privacy-policy" element={<Privacy />} />
              <Route path="/terms-of-service" element={<Terms />} />
              <Route path="/what-is-verithos" element={<WhatIsVerithos />} />
              <Route path="/sample-draft" element={<SampleReport />} />
              <Route path="/sample-report" element={<Navigate to="/sample-draft" replace />} />
              <Route path="/how-to-cite" element={<HowToCite />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/feedback" element={<Feedback />} />
              <Route path="/verithos-vs-llms" element={<VerithosVsLLMs />} />
              <Route path="/verithos-vs-chatgpt" element={<Navigate to="/verithos-vs-llms" replace />} />
              <Route path="/verithos-vs-nvivo-atlasti-maxqda-dedoose" element={<QdasComparison />} />
              <Route path="/whitepaper" element={<Whitepaper />} />
              <Route path="/whitepaper/workflow" element={<Navigate to="/whitepaper#workflow" replace />} />
              <Route path="/whitepaper/standards" element={<Navigate to="/whitepaper#standards" replace />} />
              <Route path="/whitepaper/references" element={<Navigate to="/whitepaper#references" replace />} />
              <Route path="/whitepaper/changelog" element={<Navigate to="/whitepaper#references" replace />} />
              <Route path="/v/card/:token" element={<VCardPage />} />
              <Route path="/v/board/:token" element={<VBoardPage />} />
              <Route path="/v/draft/:token" element={<VDraftPage />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />

              {/* ── Authenticated routes — wrapped in heavy providers ── */}
              <Route element={<AuthenticatedShell />}>
                <Route path="/invite/:token" element={<InvitePage />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/onboarding/role" element={<UserRoleScreen />} />
                <Route path="/onboarding/start" element={<OnboardingFlow />} />
                <Route path="/onboarding/upload" element={<OnboardingUpload />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/dashboard" element={<Navigate to="/projects" replace />} />
                <Route path="/project/:slug" element={<ProjectView />} />
                <Route path="/transcripts" element={<TranscriptLibrary />} />
                <Route path="/codebook" element={<Navigate to="/findings?tab=findings" replace />} />
                <Route path="/findings" element={<Findings />} />
                <Route path="/draft" element={<Draft />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/inbox" element={<Inbox />} />
                <Route path="/research-advisor" element={<ResearchAdvisor />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/admin" element={<AdminDashboard />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
